// services/lockApi.js
//
// Frontend API surface for the TTLock Phase 1 endpoints. Uses the shared
// secureApi instance — auth header is attached automatically. Each call
// returns the parsed response body so callers don't have to .data through it.

import { secureApi } from './secureApi';

const lockApi = {
  /**
   * Lock state + recent 20 events for a property.
   * Server gates by validatePropertyOwnership — admin/staff bypass, owners
   * see only their own properties.
   */
  getPropertyLockState: async (propertyId) => {
    const response = await secureApi.get(`/api/v1/locks/property/${propertyId}`);
    return response.data;
  },

  /**
   * ALL active locks across the fleet — fleet awareness surface.
   * Admin only. Returns each lock with computed attention reasons so the
   * client can sort/filter without fetching twice.
   */
  getActiveLocks: async () => {
    const response = await secureApi.get('/api/v1/locks/active');
    return response.data;
  },

  /**
   * Subset of /active — only locks with at least one attention reason.
   * Useful for narrower dashboard widgets / count badges.
   */
  getLocksNeedingAttention: async () => {
    const response = await secureApi.get('/api/v1/locks/needs-attention');
    return response.data;
  },

  /**
   * MERGED arrival readiness — one row per guest arriving TODAY OR TOMORROW,
   * carrying BOTH the lock-code column and the check-in-message column plus one
   * overall verdict. Two days by design: the check-in message goes out 24h
   * ahead, so the guests whose message needs attention today arrive tomorrow.
   * Composed from two independent engines; the composer aggregates their
   * verdicts and owns no rules of its own.
   */
  getTodayArrivalReadiness: async () => {
    const response = await secureApi.get('/api/v1/arrivals/readiness');
    return response.data;
  },

  /**
   * "I sent this guest their check-in instructions myself."
   *
   * Gathern / manual / direct bookings have no OTA thread, so the automation can
   * never deliver their arrival instructions — the readiness card shows them as an
   * action until a human acknowledges it. Trust-based, audit-trailed server-side.
   *
   * `preserveBackendError` so the caller can read the specific refusal (e.g. an
   * OTA booking, which must NOT be tickable) instead of secureApi's generic text.
   */
  markCheckinMessageSentManually: async (bookingId) => {
    const response = await secureApi.post(
      `/api/v1/arrivals/${bookingId}/manual-send`,
      {},
      { preserveBackendError: true }
    );
    return response.data;
  },

  /**
   * Undo a mis-tick. The acknowledgement is a human assertion with nothing to
   * verify against, so it will sometimes land on the wrong row — without this the
   * arrival would read "handled" for the rest of the stay. Both the tick and the
   * undo stay on the booking's change log.
   */
  undoCheckinMessageSentManually: async (bookingId) => {
    const response = await secureApi.delete(
      `/api/v1/arrivals/${bookingId}/manual-send`,
      { preserveBackendError: true }
    );
    return response.data;
  },

  /**
   * TTLock locks visible to the developer account that aren't yet mapped
   * to a Lock document. Admin only.
   */
  getUnmappedLocks: async () => {
    const response = await secureApi.get('/api/v1/locks/unmapped');
    return response.data;
  },

  /**
   * Map a TTLock lockId to a property. Admin only.
   * Returns 409 if either the lock is already mapped, or the property
   * already has a different active lock (explicit replacement flow required).
   */
  mapLock: async ({ lockId, propertyId }) => {
    const response = await secureApi.post('/api/v1/locks/map', { lockId, propertyId });
    return response.data;
  },

  /**
   * Soft-deactivate a Lock document by its Mongo _id. Admin only.
   * Sets status.isActive=false. The lock document and all associated
   * LockEvent records are preserved for audit history. After this,
   * the same physical lockId can be re-mapped to any property.
   */
  deactivateLock: async (lockMongoId) => {
    const response = await secureApi.patch(`/api/v1/locks/${lockMongoId}/deactivate`);
    return response.data;
  }
};

export default lockApi;
